/**
 * /channel-partner/hub/services — services of attributed customers.
 *
 * Search params: `customer` (customer id filter), `customerName` (display
 * only), `status` (Hub status group) and `serviceType` (exact service type).
 * Anything malformed is dropped.
 */
import { createFileRoute } from '@tanstack/react-router';
import type { ChannelPartnerHubStatus } from '@/api/channel-partners';
import { isHubStatus } from '@/features/channel-partner/format';
import { ChannelPartnerHubServices } from '@/features/channel-partner/components/hub-services';

interface ServicesSearch {
  customer?: string;
  customerName?: string;
  status?: ChannelPartnerHubStatus;
  serviceType?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/_protected/channel-partner/hub/services')({
  validateSearch: (search: Record<string, unknown>): ServicesSearch => ({
    customer: typeof search.customer === 'string' && UUID.test(search.customer) ? search.customer : undefined,
    customerName:
      typeof search.customerName === 'string' && search.customerName.trim()
        ? search.customerName.trim().slice(0, 120)
        : undefined,
    status: isHubStatus(search.status) ? search.status : undefined,
    serviceType:
      typeof search.serviceType === 'string' && /^[a-z0-9_-]{1,80}$/i.test(search.serviceType)
        ? search.serviceType
        : undefined,
  }),
  component: ServicesRoute,
});

function ServicesRoute() {
  const { customer, customerName, status, serviceType } = Route.useSearch();
  return (
    <ChannelPartnerHubServices
      customerId={customer}
      customerName={customer ? customerName : undefined}
      status={status}
      serviceType={serviceType}
    />
  );
}
